"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import type { PublicBranding } from "@/lib/branding/branding-context";
import { translateAuthError } from "@/lib/auth/errors";

export default function LoginForm({ branding }: { branding: PublicBranding }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const supabase = createClient();

  // Mot de passe oublié
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (authError) {
      setError(translateAuthError(authError));
      setLoading(false);
      return;
    }

    const { data: profile, error: profileError } = await supabase
      .from("profiles")
      .select("role, actif")
      .eq("email", email)
      .single();

    if (profileError || !profile) {
      setError("Impossible de récupérer votre profil");
      setLoading(false);
      return;
    }

    if (profile.actif !== true) {
      await supabase.auth.signOut();
      setError("Ce compte a été désactivé. Contactez votre responsable.");
      setLoading(false);
      return;
    }

    if (profile.role === "admin") {
      router.push("/admin");
    } else if (profile.role === "ouvrier") {
      router.push("/worker");
    } else {
      setError("Rôle inconnu");
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    setForgotError("");

    const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      // Erreur réelle (rate limit, réseau) — style rouge, jamais le vert de succès
      setForgotError(translateAuthError(error));
      setForgotLoading(false);
      return;
    }

    // Email stocké en sessionStorage — jamais en paramètre d'URL (données personnelles)
    sessionStorage.setItem("reset_email", forgotEmail.trim());
    // Redirection vers la page de réinitialisation (code ou lien, selon le gabarit)
    router.push("/reset-password");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4"
      style={{backgroundColor: "#f5f5f5"}}>
      <div className="w-full max-w-md">

        {/* Logo / titre dynamique selon le sous-domaine */}
        <div className="text-center mb-4">
          {branding.logo_url ? (
            <div className="flex justify-center mb-2">
              <img
                src={branding.logo_url}
                alt={branding.nom || "Entreprise"}
                className="h-16 md:h-20 w-auto"
              />
            </div>
          ) : (
            <div className="mb-2">
              <h1 className="text-2xl font-black uppercase tracking-widest" style={{color: branding.couleur_principale}}>
                {branding.nom || "Pointage"}
              </h1>
            </div>
          )}
        </div>

        {/* Carte */}
        <div className="bg-white rounded-xl shadow-md p-8" style={{ border: "1px solid #e5e5e5" }}>

        {forgotMode ? (
          /* ====== MOT DE PASSE OUBLIÉ ====== */
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <p className="text-sm" style={{ color: "#555" }}>
              Saisis ton adresse email. Si un compte existe, tu recevras un email pour réinitialiser ton mot de passe.
            </p>

            <div>
              <label htmlFor="forgot-email" className="block text-sm font-bold uppercase tracking-wide"
                style={{color: "#000000"}}>
                Email
              </label>
              <input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="mt-1 block w-full px-4 py-3 border-2 rounded-lg text-sm font-bold"
                style={{
                  borderColor: "#000000",
                  backgroundColor: "#ffffff",
                  color: "#000000",
                }}
                placeholder="mon.email@exemple.fr"
              />
            </div>

            {forgotError && (
              <p className="text-sm p-3 rounded-lg font-bold text-center"
                style={{
                  backgroundColor: "#fff0f0",
                  color: branding.couleur_secondaire,
                  border: `2px solid ${branding.couleur_secondaire}`,
                }}>
                {forgotError}
              </p>
            )}

            <button
              type="submit"
              disabled={forgotLoading}
              className="w-full py-3 px-4 text-white font-bold text-base uppercase tracking-wider rounded-lg transition-colors"
              style={{ backgroundColor: branding.couleur_principale }}
            >
              {forgotLoading ? "Envoi..." : "Envoyer le code"}
            </button>

            <button
              type="button"
              onClick={() => { setForgotMode(false); setForgotError(""); }}
              className="w-full text-sm font-bold text-center"
              style={{ color: branding.couleur_principale }}
            >
              ← Retour à la connexion
            </button>
          </form>
        ) : (
          /* ====== CONNEXION NORMALE ====== */
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-bold uppercase tracking-wide"
                style={{color: "#000000"}}>
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 block w-full px-4 py-3 border-2 rounded-lg text-sm font-bold"
                style={{
                  borderColor: "#000000",
                  backgroundColor: "#ffffff",
                  color: "#000000",
                }}
                placeholder="email@exemple.fr"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold uppercase tracking-wide"
                style={{color: "#000000"}}>
                Mot de passe
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 block w-full px-4 py-3 border-2 rounded-lg text-sm"
                style={{
                  borderColor: "#000000",
                  backgroundColor: "#ffffff",
                  color: "#000000",
                }}
              />
            </div>

            {error && (
              <p className="text-sm p-3 rounded-lg font-bold text-center"
                style={{
                  backgroundColor: "#fff0f0",
                  color: branding.couleur_secondaire,
                  border: `2px solid ${branding.couleur_secondaire}`,
                }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 text-white font-bold text-base uppercase tracking-wider rounded-lg transition-colors"
              style={{
                backgroundColor: loading ? "#ccc" : branding.couleur_secondaire,
              }}
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setForgotMode(true)}
                className="text-sm font-bold"
                style={{ color: branding.couleur_principale }}
              >
                Mot de passe oublié ?
              </button>
            </div>
          </form>
        )}
        </div>{/* fermeture carte */}
      </div>
    </div>
  );
}
